import { useForm, SubmitHandler, Controller } from "react-hook-form";
import s from "./PasswordSettings.module.scss";
import clsx from "clsx";
import { Button, Input } from "antd";
import EyeOpen from "../../../../../../global components/Icons/EyeOpen/EyeOpen";
import EyeClose from "../../../../../../global components/Icons/EyeClose/EyeClose";
import useSWRMutation from "swr/mutation";
import {
  parseClientError,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import { SuccessDataType } from "../../../../../../libs/api/types";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";

type Inputs = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};
function PasswordSettings() {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<Inputs>({ shouldFocusError: false });
  const newPasswordValue = watch("newPassword"); // Watch the newPassword value

  const { trigger: changePassword } = useSWRMutation(
    "api/admin/me/password",
    swrMutationFetcher,
  );
  const token = UseAuthToken();
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {

      const fetchArg: SwrMutationFetcherArgType = {
        token,
        requestOption: {
          body: JSON.stringify({
            currentPassword: data.currentPassword,
            newPassword: data.confirmPassword,
          }),
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
        },
      };
      const result: SuccessDataType<any> = await changePassword(fetchArg);
      generateResult(
        "success",
        result.message.charAt(0).toUpperCase() + result.message.slice(1),
      );
      reset();
    } catch (error) {
      const e = parseClientError(error);
      generateResult(
        "error",
        e.message.charAt(0).toUpperCase() + e.message.slice(1) || e.error,
      );
    }
  };

  return (
    <div className={s.wrapper}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={clsx(s.inputWrapper)}>
          <h5 className={clsx(s.inputLabel)}>Current password</h5>
          <Controller
            name="currentPassword"
            control={control}
            rules={{
              required: "Current password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters long",
              },
              // pattern: {
              //   value:
              //     /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#._])[A-Za-z\d@$!%*?&#._]{6,}$/,
              //   message:
              //     "Password must include at least one uppercase letter, one number, and one special character",
              // },
            }} // Validation rule
            render={({ field }) => (
              <Input.Password
                {...field} // Spread the field props here
                placeholder="input password"
                className={clsx(s.input, s.customPasswordInput, {
                  [s.inputPasswordError]: errors.currentPassword,
                })}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOpen width={16} height={16} fill="#6A6767" />
                  ) : (
                    <EyeClose width={16} height={16} fill="#6A6767" />
                  )
                }
              />
            )}
          />
          {/* Display new password error */}
          {errors.currentPassword && (
            <span className={clsx(s.errorMessage)}>
              {errors.currentPassword.message}
            </span>
          )}
        </div>
        {/* New Password Input */}
        <div className={clsx(s.inputWrapper)}>
          <h5 className={clsx(s.inputLabel)}>New password</h5>
          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: "New password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters long",
              },
              pattern: {
                value:
                  /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#._])[A-Za-z\d@$!%*?&#._]{6,}$/,
                message:
                  "Password must include at least one uppercase letter, one number, and one special character",
              },
            }} // Validation rule
            render={({ field }) => (
              <Input.Password
                {...field} // Spread the field props here
                placeholder="input password"
                className={clsx(s.input, s.customPasswordInput, {
                  [s.inputPasswordError]: errors.newPassword,
                })}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOpen width={16} height={16} fill="#6A6767" />
                  ) : (
                    <EyeClose width={16} height={16} fill="#6A6767" />
                  )
                }
              />
            )}
          />
          {/* Display new password error */}
          {errors.newPassword && (
            <span className={clsx(s.errorMessage)}>
              {errors.newPassword.message}
            </span>
          )}
        </div>

        {/* Confirm Password Input */}
        <div className={clsx(s.inputWrapper)}>
          <h5 className={clsx(s.inputLabel)}>Re-enter password</h5>
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters long",
              },
              pattern: {
                value:
                  /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#._])[A-Za-z\d@$!%*?&#._]{6,}$/,
                message:
                  "Password must include at least one uppercase letter, one number, and one special character",
              },
              validate: (value) =>
                value === newPasswordValue || "Passwords do not match", // Custom validation
            }}
            render={({ field }) => (
              <Input.Password
                {...field} // Spread the field props here
                placeholder="input password"
                className={clsx(s.input, s.customPasswordInput, {
                  [s.inputPasswordError]: errors.confirmPassword,
                })}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOpen width={16} height={16} fill="#6A6767" />
                  ) : (
                    <EyeClose width={16} height={16} fill="#6A6767" />
                  )
                }
              />
            )}
          />
          {/* Display confirm password error */}
          {errors.confirmPassword && (
            <span className={clsx(s.errorMessage)}>
              {errors.confirmPassword.message}
            </span>
          )}
        </div>

        <Button
          type="default"
          className={clsx(s.button)} // Button style
          htmlType="submit" // Ensures it's tied to form submission
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Save Changes
        </Button>
      </form>
    </div>
  );
}

export default PasswordSettings;
